// Penn State (University Park campus): public college page (#2596), official
// facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
// #3859 (2026-10-11), page vs KB: food and housing now come from Penn
// State's own cost of attendance elements ($15,856), the figure the KB
// holds; required fees stay the Common Data Set's.
//  - Penn State's University Park Common Data Set 2025-2026 (PDF): C1
//    applicants/admits/enrollees for Fall 2025 with the residency table; C8
//    test policy; C21/C22 early plans; G1/G5 2026-2027 costs. Penn State
//    publishes a separate CDS per campus; every number here is University
//    Park's. G3/G4 say tuition varies by year of study and by program.
//  - psu.edu first-year pages: dates and deadlines, admission requirements
//    (programs with extra materials), and steps to apply (test-optional
//    statement). The deadlines page prints no entry year; it is the page live
//    for the application that opened August 1, 2026.
//  - College Scorecard, school id 214777 "Pennsylvania State University-Main
//    Campus" (net price by family income), read from the Scorecard site's own
//    API response.
// Nothing here is profile-derived or anyone's odds. Re-check a
// figure against its source before editing it, and move `lastChecked` when
// you do.

export const SOURCES = {
    cds: {
        label: 'Penn State University Park Common Data Set 2025-2026',
        url: 'https://bpb-us-e1.wpmucdn.com/sites.psu.edu/dist/d/114442/files/2026/06/UniversityPark_CDS_2025_2026.pdf',
        read: '2026-09-30',
    },
    deadlines: {
        label: 'Penn State, Dates and Deadlines for First-Year Students',
        url: 'https://www.psu.edu/resources/first-year-students/deadlines',
        read: '2026-09-30',
    },
    requirements: {
        label: 'Penn State, Admissions Requirements for First-Year Students',
        url: 'https://www.psu.edu/resources/first-year-students/requirements',
        read: '2026-09-30',
    },
    steps: {
        label: 'Penn State, Steps to Apply for First-Year Students',
        url: 'https://www.psu.edu/resources/first-year-students/steps-to-apply',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?214777-Pennsylvania-State-University-Main-Campus',
        read: '2026-10-15',
    },
    tuition: {
        label: 'Penn State Tuition, Rates Effective 2026 Fall Semester (University Park), with its Cost of Attendance elements',
        url: 'https://tuition.psu.edu/rates-effective-2026-fall-semester',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'pennsylvania-state-university',
    kbId: 'pennsylvania_state_university',
    name: 'Pennsylvania State University',
    shortName: 'Penn State',
    location: 'University Park, PA',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Penn State brand site); decoration only.
    branding: {
        primary: '#001E44',
        secondary: '#96BEE6',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/pennsylvania_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 106547,
        admitted: 58952,
        enrolled: 9148,
        source: 'cds',
        where: 'section C1 (University Park campus)',
        residency: [
            { group: 'In-state', applied: 21321, admitted: 12096 },
            { group: 'Out-of-state', applied: 72861, admitted: 38846 },
            { group: 'International', applied: 12365, admitted: 8010 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Penn State\'s University Park Common Data Set reports one first-year acceptance rate for the campus, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'The Common Data Set lists early action (apply by November 1, hear by December 24) but has no field for early action counts, so it gives no early action acceptance rate. Penn State has no early decision plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most Penn State majors need only the application. These ask for more:',
        rows: [
            { school: 'Acting BFA', requirement: 'A prescreen, needed to complete the application.' },
            { school: 'Musical Theatre', requirement: 'A prescreen, needed to complete the application.' },
            { school: 'Theatre BFA', requirement: 'A prescreen, needed to complete the application.' },
            { school: 'Music', requirement: 'A School of Music audition, which you schedule to complete the application.' },
            { school: 'Graphic Design', requirement: 'Evidence of Creative Work, needed to complete the application.' },
            { school: 'Premedical-Medical BS/MD', requirement: 'A one-page resume and answers to program questions. SAT or ACT scores are mandatory for this program.' },
            { school: 'Professional Golf Management', requirement: 'Playing proficiency shown by a handicap of 12 or lower.' },
            { school: 'Schreyer Honors College', requirement: 'A separate application with essays, short answers and recommendation letters.' },
            { school: 'Millennium Scholars Program', requirement: 'A separate application with essays and recommendation letters.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular admission', value: 'Rolling after Nov 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By December 24', binding: false },
            { plan: 'Regular admission', deadline: 'Rolling after November 1', decision: 'Rolling from January', binding: false },
            { plan: 'Nursing (University Park)', deadline: 'December 1', decision: 'By January 31 (if you apply after November 1)', binding: false },
            { plan: 'Accelerated Premedical-Medical', deadline: 'October 13', decision: 'Final decisions in February', binding: false },
        ],
        note: 'Penn State calls November 1 its one big deadline. After it, applications are read on a rolling basis and University Park gets more competitive. Everyone admitted has until May 1 to accept. Penn State has no early decision plan.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Penn State is test-optional. On the application you say whether you want scores used; if you do, they are read. Penn State uses your highest combined score from a single test date and does not superscore. The Premedical-Medical BS/MD program requires the SAT or ACT.',
        source: 'steps',
        cdsLine: 'The Common Data Set agrees: the SAT or ACT is not required for admission but is considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (first year)', amounts: [20878, 44574] },
            { item: 'Required fees', amounts: [640, 640], source: 'cds' },
            { item: 'Food and housing (on campus)', amounts: [15856, 15856] },
            { item: 'Books and supplies', amounts: [1290, 1290] },
            { item: 'Transportation', amounts: [2244, 2244] },
            { item: 'Other expenses', amounts: [3112, 3112] },
        ],
        totals: [44020, 67716],
        note: 'University Park, freshman and sophomore tuition. Food, housing, books, transportation and other expenses are Penn State\'s per-semester cost of attendance figures times two. Required fees are from the Common Data Set.',
        source: 'tuition',
        netPrice: {
            average: 33675,
            byIncome: [
                { income: '$0 to $30,000', amount: 22193 },
                { income: '$30,001 to $48,000', amount: 21267 },
                { income: '$48,001 to $75,000', amount: 25458 },
                { income: '$75,001 to $110,000', amount: 31948 },
                { income: 'Over $110,000', amount: 39140 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes, for the University Park campus. Out-of-state students pay more. Your own price comes from Penn State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
